/**
 * Photo / media pipeline (E1, spec 2026-06-10-photo-media-pipeline-design.md).
 *
 * The privacy boundary is the client: EXIF/XMP is stripped HERE, before any
 * encryption or upload, regardless of what the browser image processor did
 * (spec §2.1, "non-negotiable, client-enforced"). Photos seal under the
 * report's own broadcast key epoch (identical keyRef) so shredding the report
 * kills the photos (spec §1). Blobs upload first; the report then references
 * content-addressed ids — the log never points at a blob that does not exist.
 */
import type { BeaconClient } from "./client.js";
import type { SealedBroadcastJson } from "./seal-helpers.js";
import { stdB64Decode } from "./model.js";

export type ImageMime = "image/jpeg" | "image/webp";

// --- EXIF / XMP stripping (the test that must never regress, spec §7) ---

// JPEG markers that may carry metadata or are non-essential → dropped:
// APP0..APP15 (0xE0-0xEF, incl. EXIF/XMP/JFIF/ICC) and COM (0xFE).
function isJpegDropMarker(m: number): boolean {
  return (m >= 0xe0 && m <= 0xef) || m === 0xfe;
}

function stripExifJpeg(b: Uint8Array): Uint8Array {
  if (b.length < 2 || b[0] !== 0xff || b[1] !== 0xd8) return b; // not JPEG; leave untouched
  const out: number[] = [0xff, 0xd8];
  let i = 2;
  while (i < b.length) {
    if (b[i] !== 0xff) { out.push(b[i]); i++; continue; }
    // A marker is ONE OR MORE 0xFF fill bytes (ISO 10918-1 §B.1.1.2) followed by
    // a non-0xFF marker code. Skip the fill run before reading the marker —
    // otherwise `0xFF 0xFF 0xE1 …` is misread and the EXIF segment leaks through.
    let m = i + 1;
    while (m < b.length && b[m] === 0xff) m++;
    if (m >= b.length) break; // trailing fill bytes, no marker
    const marker = b[m];
    // SOS (0xDA): entropy-coded scan follows with no length — copy marker + rest.
    if (marker === 0xda) {
      out.push(0xff);
      for (let j = m; j < b.length; j++) out.push(b[j]);
      return new Uint8Array(out);
    }
    // Standalone markers with no length field (EOI/RSTn/TEM): emit canonical 0xFF+code.
    if (marker === 0xd9 || (marker >= 0xd0 && marker <= 0xd7) || marker === 0x01) {
      out.push(0xff, marker); i = m + 1; continue;
    }
    if (m + 2 >= b.length) break; // truncated length field
    const len = (b[m + 1] << 8) | b[m + 2]; // includes the 2 length bytes
    const end = Math.min(m + 1 + len, b.length);
    if (!isJpegDropMarker(marker)) { out.push(0xff); for (let j = m; j < end; j++) out.push(b[j]); }
    i = end;
  }
  return new Uint8Array(out);
}

const FOURCC = (b: Uint8Array, off: number) =>
  String.fromCharCode(b[off], b[off + 1], b[off + 2], b[off + 3]);
const WEBP_DROP = new Set(["EXIF", "XMP "]);

function stripExifWebp(b: Uint8Array): Uint8Array {
  if (b.length < 12 || FOURCC(b, 0) !== "RIFF" || FOURCC(b, 8) !== "WEBP") return b;
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const kept: Uint8Array[] = [];
  let i = 12;
  while (i + 8 <= b.length) {
    const fourcc = FOURCC(b, i);
    const len = dv.getUint32(i + 4, true);
    const padded = len + (len % 2); // chunks are even-padded
    const end = Math.min(i + 8 + padded, b.length);
    if (!WEBP_DROP.has(fourcc)) kept.push(b.subarray(i, end));
    i = end;
  }
  let bodyLen = 0;
  for (const c of kept) bodyLen += c.length;
  const out = new Uint8Array(12 + bodyLen);
  out.set([0x52, 0x49, 0x46, 0x46], 0); // "RIFF"
  new DataView(out.buffer).setUint32(4, bodyLen + 4, true); // file size = body + "WEBP"
  out.set([0x57, 0x45, 0x42, 0x50], 8); // "WEBP"
  let o = 12;
  for (const c of kept) { out.set(c, o); o += c.length; }
  return out;
}

/** Remove all EXIF/XMP/metadata. Non-negotiable, client-enforced (spec §2.1). */
export function stripExif(bytes: Uint8Array, mime: ImageMime): Uint8Array {
  return mime === "image/webp" ? stripExifWebp(bytes) : stripExifJpeg(bytes);
}

// --- content addressing + upload pipeline ---

export interface ProcessedImage {
  full: Uint8Array; // downscaled/recompressed full image (browser), pre-strip
  thumb: Uint8Array; // <=256px thumbnail (browser), pre-strip
  blurhashBytes: Uint8Array; // ~30-byte blurhash string as bytes
  mime: ImageMime;
}

export interface MediaRefJson {
  blob: string;
  thumb?: string;
  blurhash?: SealedBroadcastJson;
  mime: ImageMime;
  bytes: number;
}

export interface BlobUploader {
  upload(ciphertext: Uint8Array, mime: ImageMime): Promise<{ blobId: string }>;
}

const MAX_MEDIA = 3; // spec §4/§6
const MAX_FULL_CT = 1.25 * 1024 * 1024; // spec §6
const MAX_THUMB_CT = 48 * 1024; // spec §6
const MAX_BLURHASH_CT = 128; // spec §4 (matches lexicon x-beacon-max-ciphertext)

export async function sha256Hex(b: Uint8Array): Promise<string> {
  const d = new Uint8Array(await crypto.subtle.digest("SHA-256", b));
  return Array.from(d, (x) => x.toString(16).padStart(2, "0")).join("");
}

/**
 * Client photo pipeline (spec §2): strip EXIF/XMP, seal full+thumb+blurhash
 * under the report's keyRef snapshot, upload ciphertext (blobs-first), and
 * return refs the report event will carry. Self-verifies the server's
 * content address (blobId === sha256(ciphertext)).
 *
 * A failed photo never fails the report (spec §6) — the caller wraps this and
 * publishes without media on throw; see report-publish.ts.
 */
export async function buildMediaRefs(
  client: BeaconClient,
  snap: { keyRef: string; key: Uint8Array },
  images: ProcessedImage[],
  uploader: BlobUploader,
): Promise<MediaRefJson[]> {
  if (images.length > MAX_MEDIA) throw new Error(`media: max ${MAX_MEDIA} photos per report`);
  const refs: MediaRefJson[] = [];
  for (const img of images) {
    const full = stripExif(img.full, img.mime);
    const thumb = stripExif(img.thumb, img.mime);

    const fullSealed = await client.sealBroadcastWith(snap, full);
    const thumbSealed = await client.sealBroadcastWith(snap, thumb);
    const blurhashSealed = await client.sealBroadcastWith(snap, img.blurhashBytes);

    const fullCt = stdB64Decode(fullSealed.ciphertext.$bytes);
    const thumbCt = stdB64Decode(thumbSealed.ciphertext.$bytes);
    const blurhashCt = stdB64Decode(blurhashSealed.ciphertext.$bytes);
    if (fullCt.length > MAX_FULL_CT) throw new Error("media: full ciphertext exceeds 1.25 MiB");
    if (thumbCt.length > MAX_THUMB_CT) throw new Error("media: thumb ciphertext exceeds 48 KiB");
    if (blurhashCt.length > MAX_BLURHASH_CT) throw new Error("media: blurhash exceeds 128 B");

    const { blobId } = await uploader.upload(fullCt, img.mime);
    if (blobId !== (await sha256Hex(fullCt))) throw new Error("media: server blobId != sha256(ciphertext)");
    const { blobId: thumbId } = await uploader.upload(thumbCt, img.mime);
    if (thumbId !== (await sha256Hex(thumbCt))) throw new Error("media: server thumbId != sha256(ciphertext)");

    refs.push({ blob: blobId, thumb: thumbId, blurhash: blurhashSealed, mime: img.mime, bytes: fullCt.length });
  }
  return refs;
}
